import { Helmet } from 'react-helmet';

import { MaoriLanguage } from '@/components/Accessibility/MaoriLanguage';
import { LegalBody2, LegalH2, LegalPageWrapper, LegalUl } from '@/components/LegalPages/LegalPages';
import { H2 } from '@/components/Typography/Typography';
import { Fineprint } from '@/components/Typography/Typography';

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Tātai | Privacy policy</title>
        <meta
          name="description"
          content="Tātai is a voluntary service that collects iwi affiliation information to support Māori health and wellbeing. This Privacy Statement explains how your data is collected, protected, and used under Māori-led governance and Te Tiriti o Waitangi principles."
        />
        <meta
          property="og:description"
          content="Tātai is a voluntary service that collects iwi affiliation information to support Māori health and wellbeing. This Privacy Statement explains how your data is collected, protected, and used under Māori-led governance and Te Tiriti o Waitangi principles."
        />
      </Helmet>
      <LegalPageWrapper>
        <H2 as="h1">Privacy Policy</H2>

        <LegalH2>Introduction</LegalH2>
        <LegalBody2>
          Welcome to the <MaoriLanguage>Tātai</MaoriLanguage> Privacy Statement. <MaoriLanguage>Tātai</MaoriLanguage> is
          a voluntary service that collects iwi affiliation information to support <MaoriLanguage>Māori</MaoriLanguage>{' '}
          health and wellbeing. This Privacy Statement explains how your data is collected, protected, and used under{' '}
          <MaoriLanguage>Māori</MaoriLanguage>-led governance and Te Tiriti o Waitangi principles.
        </LegalBody2>
        <LegalBody2>
          Welcome to <MaoriLanguage>Tātai</MaoriLanguage>, a collection of iwi affiliation information created in
          partnership with <MaoriLanguage>Māori</MaoriLanguage> for <MaoriLanguage>Māori</MaoriLanguage>.
        </LegalBody2>
        <LegalBody2>
          It is a voluntary collection of information from <MaoriLanguage>Māori</MaoriLanguage> who wish to participate
          in the <MaoriLanguage>Tātai</MaoriLanguage> project and identify the iwi they affiliate with in Aotearoa New
          Zealand.
        </LegalBody2>
        <LegalBody2>
          The intent of <MaoriLanguage>Tātai</MaoriLanguage> is to use iwi affiliation data to improve{' '}
          <MaoriLanguage>Māori</MaoriLanguage> health and wellbeing through more informed decision-making and
          evidence-based practices. <MaoriLanguage>Tātai</MaoriLanguage> aims to strengthen understanding of iwi
          connections to support <MaoriLanguage>whānau</MaoriLanguage> wellbeing now and for future generations, by
          safely sharing deidentified data with iwi to enable informed decision making by iwi.
        </LegalBody2>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> acknowledges whakapapa, or genealogy, is a fundamental principle for{' '}
          <MaoriLanguage>Māori</MaoriLanguage>. With the understanding that iwi affiliation data is a taonga,{' '}
          <MaoriLanguage>Tātai</MaoriLanguage> is designed to ensure the mana of contributors and their{' '}
          <MaoriLanguage>tūpuna</MaoriLanguage> is upheld by every person engaging with the data collected in{' '}
          <MaoriLanguage>Tātai</MaoriLanguage>.
        </LegalBody2>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> is delivered in partnership with{' '}
          <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health, Te Whatu Ora - Health NZ and guided by a{' '}
          <MaoriLanguage>Māori</MaoriLanguage>-led governance group. This group includes representatives from:
        </LegalBody2>
        <LegalUl>
          <li>
            <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health
          </li>
          <li>Te Whatu Ora - Health NZ</li>
          <li>
            <MaoriLanguage>Te Kāhui Raraunga</MaoriLanguage>
          </li>
          <li>
            <MaoriLanguage>Te Puni Kōkiri</MaoriLanguage>
          </li>
          <li>Stats NZ</li>
          <li>Ministry of Education.</li>
        </LegalUl>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> is grounded in the principles of Te Tiriti o Waitangi. These principles
          guide how iwi affiliation data is collected, protected, and used in partnership with{' '}
          <MaoriLanguage>Māori</MaoriLanguage>, upholding tino rangatiratanga, equity, active protection, options, and
          partnership.
        </LegalBody2>
        <LegalBody2>
          This service is governed by Aotearoa New Zealand law and the principles of manaakitanga, kaitiakitanga, and
          whakapapa.
        </LegalBody2>

        <LegalH2>Purpose of the service</LegalH2>
        <LegalBody2>The purpose of the service is to:</LegalBody2>
        <LegalUl>
          <li>
            collect iwi affiliation information to improve <MaoriLanguage>Māori</MaoriLanguage> health outcomes
          </li>
          <li>
            support oversight by a <MaoriLanguage>Māori</MaoriLanguage>-led governance group that includes iwi and
            government representatives
          </li>
          <li>
            provide aggregated, non-identifiable data to iwi and government agencies for statistical, research, and
            planning purposes
          </li>
          <li>
            ensure iwi affiliation information contributes to more equitable health outcomes while protecting
            individuals&apos; privacy.
          </li>
        </LegalUl>

        <LegalH2>
          Data sharing and <MaoriLanguage>Māori</MaoriLanguage> data governance
        </LegalH2>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> supports <MaoriLanguage>Māori</MaoriLanguage> data sovereignty and
          kaupapa <MaoriLanguage>Māori</MaoriLanguage> decision-making.
        </LegalBody2>
        <LegalBody2>At present, your information may be shared in the following ways:</LegalBody2>
        <LegalUl>
          <li>
            with the iwi you whakapapa to, in aggregated and de-identified form only, this protects your privacy while
            supporting iwi decision-making
          </li>
          <li>
            with <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health and Te Whatu Ora - Health NZ, under
            strict governance
          </li>
          <li>
            with trusted research and policy partners, but only when anonymised and with{' '}
            <MaoriLanguage>Māori</MaoriLanguage> oversight.
          </li>
        </LegalUl>
        <LegalBody2>
          Any future sharing of identifiable information with iwi would require explicit consent from you and approval
          from the <MaoriLanguage>Māori</MaoriLanguage>-led governance group.
        </LegalBody2>
        <LegalBody2>
          A <MaoriLanguage>Māori</MaoriLanguage>-led governance group provides oversight and ensures all use of data
          aligns with the purpose of improving <MaoriLanguage>Māori</MaoriLanguage> health and wellbeing, while
          custodianship of the information currently remains with <MaoriLanguage>Manatū Hauora</MaoriLanguage> -
          Ministry of Health and Te Whatu Ora - Health NZ.
        </LegalBody2>
        <LegalBody2>
          The governance group includes <MaoriLanguage>Te Kāhui Raraunga</MaoriLanguage>, established by the National
          Iwi Chairs Forum in 2016 to advocate for iwi access, ownership, and control over data. The group consists of
          iwi representatives focused on advancing the data kaupapa with the Crown.
        </LegalBody2>
        <LegalBody2>
          This service is guided by kaupapa <MaoriLanguage>Māori</MaoriLanguage> values and aligned with Te Tiriti o
          Waitangi. It upholds <MaoriLanguage>Māori</MaoriLanguage> data sovereignty through iwi-led governance,
          ensuring that any use of information respects whakapapa and iwi decision-making.
        </LegalBody2>
        <LegalBody2>
          You own your iwi affiliation information. You may edit, update, or delete it any time by logging in through My
          Health Account.
        </LegalBody2>
        <LegalBody2>
          You may also submit for another person with their consent or if you are their legal guardian. If done without
          My Health Account, it will not link to your account, and they can later claim and manage it through their own
          login.
        </LegalBody2>

        <LegalH2>What information we collect</LegalH2>
        <LegalBody2>
          We collect only the information needed to support the goals of this project, which is grounded in{' '}
          <MaoriLanguage>Māori</MaoriLanguage> values and designed in partnership with{' '}
          <MaoriLanguage>Māori</MaoriLanguage>.
        </LegalBody2>
        <LegalBody2>Required information:</LegalBody2>
        <LegalUl>
          <li>first name</li>
          <li>last name</li>
          <li>date of birth</li>
          <li>email and/or phone number</li>
          <li>
            whether you are of <MaoriLanguage>Māori</MaoriLanguage> descent (Yes / No / Do not know)
          </li>
          <li>whether you know your iwi name(s) (Yes / No / I know some of them).</li>
        </LegalUl>
        <LegalBody2>
          If you are unsure of your iwi, a searchable map and list of iwi by region is provided to support your journey.
          You can still indicate <MaoriLanguage>Māori</MaoriLanguage> descent without selecting a specific iwi.
        </LegalBody2>
        <LegalBody2>Optional information:</LegalBody2>
        <LegalUl>
          <li>iwi affiliation(s)</li>
          <li>NHI number (if known)</li>
          <li>middle name(s)</li>
          <li>gender</li>
          <li>address.</li>
        </LegalUl>
        <LegalBody2>
          You can choose to provide your NHI number if you know it. If you do not provide it, your details may still be
          matched to your NHI through My Health Account or the Consumer Population Identification &amp; Register (CPIR)
          process.
        </LegalBody2>

        <LegalH2>How we use your information</LegalH2>
        <LegalBody2>
          We at Te Whatu Ora - Health NZ use your name, date of birth, and contact details (and address and gender, if
          you provide these) to match your <MaoriLanguage>Tātai</MaoriLanguage> information with your NHI number. Once
          matched, your NHI may be used to link your iwi affiliation information to health information held in other
          collections managed by <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health and Te Whatu Ora -
          Health NZ.
        </LegalBody2>
        <LegalBody2>
          This enables the creation of meaningful data resources that highlight where{' '}
          <MaoriLanguage>Māori</MaoriLanguage> are interacting with the health system and the health challenges they are
          experiencing. This intends to support the delivery of health services and resources to meet{' '}
          <MaoriLanguage>Māori</MaoriLanguage> health needs.
        </LegalBody2>
        <LegalBody2>
          Currently, your information is used to match with your NHI and to generate aggregated statistical data. Our
          intention is that, in future and subject to <MaoriLanguage>Māori</MaoriLanguage>-led governance approval,
          deidentified data may also be used to:
        </LegalBody2>
        <LegalUl>
          <li>support iwi in planning services and making decisions</li>
          <li>
            generate anonymous statistical insights to improve <MaoriLanguage>Māori</MaoriLanguage> health outcomes
          </li>
          <li>
            support <MaoriLanguage>Māori</MaoriLanguage> to plan programmes, services, interventions, and policies for
            their own people
          </li>
          <li>assist in iwi planning, including social and economic development</li>
          <li>
            enable iwi, government agencies, NGOs, researchers, and iwi organisations to evaluate, monitor, plan, and
            provide services.
          </li>
        </LegalUl>
        <LegalBody2>
          Your <MaoriLanguage>Tātai</MaoriLanguage> information will only be combined with other datasets where the{' '}
          <MaoriLanguage>Māori</MaoriLanguage>-led governance group agrees that it is appropriate. You will not be
          identifiable in any statistical information created.
        </LegalBody2>
        <LegalBody2>
          All statistical information will be aggregated and non-identifiable to ensure individuals cannot be
          identified, even in small groups or unusual conditions.
        </LegalBody2>

        <LegalH2>How we keep your information safe</LegalH2>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> recognises iwi affiliation data as a taonga. We are committed to treating
          your personal information with care and respect, in accordance with the Privacy Act 2020 and the Health
          Information Privacy Code 2020.
        </LegalBody2>
        <LegalBody2>
          Your information is stored in a secure, cloud-based database located in Sydney, Australia. This platform was
          selected for its high level of security and compliance with Aotearoa New Zealand Government,{' '}
          <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health, and Te Whatu Ora - Health NZ standards.
        </LegalBody2>
        <LegalBody2>
          Our intention is to repatriate this data to Aotearoa New Zealand in the future, in alignment with{' '}
          <MaoriLanguage>Māori</MaoriLanguage> data sovereignty principles and under the guidance of the{' '}
          <MaoriLanguage>Māori</MaoriLanguage>-led governance group.
        </LegalBody2>
        <LegalBody2>
          Access to your information is strictly limited to authorised project team members and used only for approved
          collective analysis to improve <MaoriLanguage>Māori</MaoriLanguage> health outcomes.
        </LegalBody2>
        <LegalBody2>
          The system is monitored for security vulnerabilities, and identified issues are triaged and addressed through
          routine maintenance or, when necessary, urgent fixes to maintain protection.
        </LegalBody2>
        <LegalBody2>
          Any information requests will be managed in line with the Official Information Act 1982 and the Privacy Act
          2020.
        </LegalBody2>
        <LegalBody2>
          If you identify a vulnerability or security issue, you can report it by emailing{' '}
          <a href="mailto:contact@tatai.maori.nz">contact@tatai.maori.nz</a>. You can also submit a report at the{' '}
          <a href="https://www.ncsc.govt.nz/" target="_blank" rel="noopener noreferrer">
            National Cyber Security Centre
          </a>
          .
        </LegalBody2>

        <LegalH2>Who can see your information?</LegalH2>
        <LegalH2>Identifiable information</LegalH2>
        <LegalUl>
          <li>
            only authorised staff from <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health and Te Whatu
            Ora - Health NZ may access identifiable information
          </li>
          <li>
            access is limited to approved purposes that support <MaoriLanguage>Tātai</MaoriLanguage>
          </li>
          <li>at present, no identifiable information is shared outside these agencies.</li>
        </LegalUl>
        <LegalH2>Aggregated, non-identifiable information</LegalH2>
        <LegalUl>
          <li>may be prepared in future for iwi and trusted research or policy partners</li>
          <li>
            any use will be under <MaoriLanguage>Māori</MaoriLanguage>-led governance group oversight
          </li>
          <li>
            to support service planning, policy development, and improving <MaoriLanguage>Māori</MaoriLanguage> health
            outcomes.
          </li>
        </LegalUl>
        <LegalH2>Future identifiable information</LegalH2>
        <LegalBody2>Any sharing of identifiable information with iwi would require:</LegalBody2>
        <LegalUl>
          <li>
            approval from the <MaoriLanguage>Māori</MaoriLanguage>-led governance group, and
          </li>
          <li>your explicit consent.</li>
        </LegalUl>
        <LegalH2>Other uses</LegalH2>
        <LegalBody2>Any new requests to use information for other purposes will:</LegalBody2>
        <LegalUl>
          <li>be considered in partnership with iwi representatives, and</li>
          <li>be managed in line with existing legislative requirements.</li>
        </LegalUl>

        <LegalH2>How long we keep your information</LegalH2>
        <LegalBody2>
          Information held in <MaoriLanguage>Tātai</MaoriLanguage> is expected to be retained in perpetuity, in
          recognition of the whakapapa and intergenerational links associated with iwi data, unless you request that
          your information be removed.
        </LegalBody2>
        <LegalBody2>
          The <MaoriLanguage>Māori</MaoriLanguage>-led governance group provides oversight and confirms retention
          settings as part of its role.
        </LegalBody2>
        <LegalBody2>
          If you ask for your information to be removed, it will be securely deleted from{' '}
          <MaoriLanguage>Tātai</MaoriLanguage>. We will not be able to alter or remove information from any statistical
          datasets created before your deletion request. You will not be identifiable in those datasets.
        </LegalBody2>

        <LegalH2>How you can access, update, or delete your information</LegalH2>
        <LegalBody2>
          If you logged in and authenticated your submission through My Health Account, you can access and update your
          information by logging in again.
        </LegalBody2>
        <LegalBody2>
          If you originally submitted your information without logging in to My Health Account, but later log in using
          My Health Account, your data may have been matched through Consumer Population Identification &amp; Register
          (CPIR) process. If matched, you will be able to access and update your information through My Health Account.
        </LegalBody2>
        <LegalBody2>
          If you submitted information without logging in and do not have a My Health Account, you can request access to
          or deletion of your information by contacting{' '}
          <a href="mailto:contact@tatai.maori.nz">contact@tatai.maori.nz</a>.
        </LegalBody2>
        <LegalBody2>
          We may ask for proof of identity to ensure we do not release information to anyone not authorised.
        </LegalBody2>
        <LegalBody2>
          Some users may not receive direct notifications if their contact details are missing or out of date, so
          updates will be published on the <MaoriLanguage>Tātai</MaoriLanguage> website.
        </LegalBody2>
        <LegalBody2>
          To understand how your information is managed when using My Health Account, please read the{' '}
          <a href="https://www.healthnz.govt.nz/websites-and-apps/my-health-account/my-health-account-terms-and-conditions">
            My Health Account Privacy Statement
          </a>
          .
        </LegalBody2>

        <LegalH2>Statistical information our website collects</LegalH2>
        <LegalBody2>
          We may collect statistical information to help improve the <MaoriLanguage>Tātai</MaoriLanguage> web form and
          understand how it is being used. This information is aggregated and does not identify anyone personally.
        </LegalBody2>
        <LegalBody2>
          We use Google Analytics and Google Tag Manager on <MaoriLanguage>Tātai</MaoriLanguage> to collect and analyse
          details about how people interact with the site. These tools collect information such as:
        </LegalBody2>
        <LegalUl>
          <li>your IP address</li>
          <li>pages accessed on the website</li>
          <li>links clicked</li>
          <li>date and time of visit</li>
          <li>referring website (if any)</li>
          <li>your operating system and web browser</li>
          <li>other details such as screen resolution and language setting</li>
        </LegalUl>
        <LegalBody2>
          In addition, event data such as session start and stop, page views, whether a person indicated{' '}
          <MaoriLanguage>Māori</MaoriLanguage> descent, and whether a form was abandoned are also captured to help us
          improve the service.
        </LegalBody2>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> uses HTTP cookies to remember what you have done on the site during
          previous visits. No effort is made to identify individual users. You may opt out of Google Analytics by
          disabling cookies in your browser or by installing the{' '}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            Google Analytics Opt-out Browser Add-on
          </a>
          .
        </LegalBody2>
        <LegalBody2>
          To learn more, you can also read{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google&apos;s Analytics Privacy Policy
          </a>
          .
        </LegalBody2>

        <LegalH2>Contact us</LegalH2>
        <LegalBody2>
          If you have questions about your information or this Privacy Statement, you can contact the Privacy Officer at{' '}
          <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health by email at{' '}
          <a href="mailto:information@health.govt.nz">information@health.govt.nz</a> or by writing to: Privacy Officer,
          Ministry of Health, PO Box 5013, Wellington.
        </LegalBody2>
        <LegalBody2>
          You may also use <a href="mailto:contact@tatai.maori.nz">contact@tatai.maori.nz</a> for general{' '}
          <MaoriLanguage>Tātai</MaoriLanguage> enquiries, these will be directed to the appropriate team.
        </LegalBody2>
        <LegalBody2>
          If you identify a vulnerability or security issue, you can report it by emailing{' '}
          <a href="mailto:contact@tatai.maori.nz">contact@tatai.maori.nz</a>. You can also submit a report at the{' '}
          <a href="https://www.ncsc.govt.nz/" target="_blank" rel="noopener noreferrer">
            National Cyber Security Centre
          </a>
          .
        </LegalBody2>
        <LegalBody2>
          If you are not satisfied with our response, you can contact the{' '}
          <a href="https://privacy.org.nz/about-us/contact-us" target="_blank" rel="noopener noreferrer">
            Office of the Privacy Commissioner
          </a>
          .
        </LegalBody2>
        <LegalBody2>
          Where possible, we will also notify users of significant changes. If there are changes to how your information
          is used, this Privacy Statement will be updated and a notice will be published on the{' '}
          <MaoriLanguage>Tātai</MaoriLanguage> website.
        </LegalBody2>

        <Fineprint>Last updated: 9 April 2026</Fineprint>
      </LegalPageWrapper>
    </>
  );
};

export default PrivacyPolicyPage;
